import {useQuery} from "@tanstack/react-query";
import {getCompanyFeatureFlags} from "../services/featureFlagService";
import {FeatureFlagEnum} from "../types/featureFlagsEnum";
import {useEffect} from "react";
import {useAtom, useAtomValue} from "jotai";
import {showAllCalendarDatesFeatureFlagAtom} from "../store/activeDateStore";
import {routerCompanyIdAtom} from "../store/routerStore";
import {useCompany} from "./companiesHooks";

/**
 * Returns true or false based on the feature flag for the given company. All company flags are cached for 5 minutes.
 */
export function useCompanyFeatureFlag(flagId: FeatureFlagEnum) {
    const companyId = useAtomValue(routerCompanyIdAtom);
    const query = useQuery({
        queryKey: ["company-feature-flags", companyId],
        queryFn: async () => (companyId ? await getCompanyFeatureFlags(companyId) : {}),
        staleTime: 1000 * 60 * 5,
    });

    return query.data?.[flagId] === true;
}

/**
 * Returns the first parent company ID that is true for the given flagId, if none are found returns null
 */
export function useFindFirstParentCompanyFeatureFlag(flagId: FeatureFlagEnum) {
    const companyFeatureFlag = useCompanyFeatureFlag(flagId);
    const company = useCompany();
    const parentCompanyIds = company?.data?.parentCompaniesIds ?? [];

    const query = useQuery({
        queryKey: ["parent-companies-feature-flags", ...parentCompanyIds],
        queryFn: async () => {
            const results = await Promise.all(parentCompanyIds.map((id) => getCompanyFeatureFlags(id)));
            const index = results.findIndex((flags) => flags[flagId] === true);
            return index !== -1 ? parentCompanyIds[index] : null;
        },
        staleTime: 1000 * 60 * 5,
        enabled: !companyFeatureFlag && parentCompanyIds.length > 0,
    });

    return query.data;
}

/**
 * Check the company feature flags and set the showAllCalendarDatesFeatureFlagAtom value.
 *
 * The atom value is undefined to be able to differentiate between the feature flag not being enabled and it not being fetched yet, necessary to avoid certain HTTP calls until the feature flag is fetched and the atom value is set.
 */
export function useShowAllCalendarDatesFeatureFlag() {
    const companyId = useAtomValue(routerCompanyIdAtom);
    const [showAllDatesFeatureFlag, setShowAllDatesFeatureFlag] = useAtom(showAllCalendarDatesFeatureFlagAtom);
    const query = useQuery({
        queryKey: ["company-feature-flags", companyId],
        queryFn: async () => (companyId ? await getCompanyFeatureFlags(companyId) : {}),
        staleTime: 1000 * 60 * 5,
    });

    useEffect(() => {
        if (query.isFetched && companyId && query.data) {
            setShowAllDatesFeatureFlag(query.data[FeatureFlagEnum.showAllCalendarDates] === true);
        }
    }, [companyId, query.data, query.isFetched, setShowAllDatesFeatureFlag]);

    return showAllDatesFeatureFlag;
}
